import React, { FC, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ShoppingOutlined, RightOutlined } from "@ant-design/icons";
import { Spin } from "antd";

import {
  selectOrders,
  selectIsOrdersLoading,
} from "../../../redux-toolkit/orders/orders-selector";
import { resetOrders } from "../../../redux-toolkit/orders/orders-slice";
import { fetchUserOrdersByEmail } from "../../../redux-toolkit/orders/orders-thunks";
import { RootState, AppDispatch } from "../../../redux-toolkit/store";

import "../OrdersList/OrdersList.css";

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  PENDING:    { label: "Pending",    color: "#e65100", bg: "#fff3e0" },
  CONFIRMED:  { label: "Confirmed",  color: "#1565c0", bg: "#e3f2fd" },
  PROCESSING: { label: "Processing", color: "#6a1b9a", bg: "#f3e5f5" },
  SHIPPED:    { label: "Shipped",    color: "#00695c", bg: "#e0f2f1" },
  DELIVERED:  { label: "Delivered",  color: "#2e7d32", bg: "#e8f5e9" },
  FULFILLED:  { label: "Fulfilled",  color: "#2e7d32", bg: "#e8f5e9" },
  CANCELLED:  { label: "Cancelled",  color: "#c62828", bg: "#ffebee" },
  RETURNED:   { label: "Returned",   color: "#4e342e", bg: "#efebe9" },
};

const getStatusConfig = (status: string) =>
  STATUS_CONFIG[status?.toUpperCase()] ?? { label: status ?? "Unknown", color: "#555", bg: "#f5f5f5" };

const na = (val: any): string =>
  val !== null && val !== undefined && String(val).trim() !== "" ? String(val) : "Not Available";

const PersonalOrdersList: FC = (): ReactElement => {
  const dispatch  = useDispatch<AppDispatch>();
  const history   = useHistory();
  const orders    = useSelector(selectOrders);
  const isLoading = useSelector(selectIsOrdersLoading);
  const userEmail = useSelector((state: RootState) => state.user.user?.email);

  useEffect(() => {
    if (userEmail) {
      dispatch(fetchUserOrdersByEmail({ email: userEmail, page: 0 }));
    }
    return () => {
      dispatch(resetOrders());
    };
  }, [dispatch, userEmail]);

  return (
    <div className="ol-page">
      <div className="ol-header">
        <ShoppingOutlined className="ol-header-icon" />
        <span className="ol-header-title">My Orders</span>
        {!isLoading && (
          <span className="ol-header-count">
            {orders.length} order{orders.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="ol-content">
        {isLoading ? (
          <div className="ol-center"><Spin size="large" /></div>
        ) : orders.length === 0 ? (
          <div className="ol-empty">
            <div className="ol-empty-icon">📦</div>
            <p className="ol-empty-title">No orders yet</p>
            <p className="ol-empty-sub">Your orders will appear here once you place them</p>
            <button className="ol-shop-btn" onClick={() => history.push("/menu")}>
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="ol-list">
            {orders.map((order: any) => {
              const statusCfg    = getStatusConfig(order.deliveryStatus ?? order.orderStatus ?? order.status ?? "");
              const total        = order.totalPrice ?? order.amount ?? order.total;
              const payment      = order.paymentType ?? order.paymentMethod ?? "";
              const products     = order.perfumes ?? order.items ?? order.orderItems ?? [];
            const rawDate = order.createdAt ?? order.date ?? order.orderDate;

            const dateStr = rawDate
              ? new Date(rawDate).toLocaleString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true, // use false if you want 24-hour format
                })
              : "—";
              const customerName = [order.firstName, order.lastName].filter(Boolean).join(" ") || null;

              const handleCardClick = () => {
                history.push(`/account/user/orders/${order.id}`);
              };

              return (
                <div
                  key={order.id}
                  className="ol-card"
                  onClick={handleCardClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
                >
                  {/* TOP ROW */}
                  <div className="ol-card-top">
                    <div className="ol-order-meta">
                      <span className="ol-order-id">Order #{order.id}</span>
                      <span className="ol-order-date">{dateStr}</span>
                    </div>
                    <span
                      className="ol-status-badge"
                      style={{ color: statusCfg.color, background: statusCfg.bg }}
                    >
                      {statusCfg.label}
                    </span>
                  </div>

                  {/* CUSTOMER + ORDER TYPE ROW */}
                  <div className="ol-info-row">
                    {customerName && (
                      <span className="ol-info-item">
                        <span className="ol-info-label">Customer:</span> {customerName}
                      </span>
                    )}
                    <span className="ol-info-item">
                      <span className="ol-info-label">Type:</span>{" "}
                      {na(order.orderType)}
                    </span>
                  </div>

                  {/* PRODUCT THUMBNAILS */}
                  {products.length > 0 && (
                    <div className="ol-thumbs">
                      {products.slice(0, 4).map((p: any, i: number) => (
                        <div key={i} className="ol-thumb-wrap">
                          <img
                            src={p.filename ?? p.image ?? p.imageUrl ?? ""}
                            alt={p.perfumeTitle ?? p.name ?? ""}
                            className="ol-thumb"
                          />
                        </div>
                      ))}
                      {products.length > 4 && (
                        <div className="ol-thumb-more">+{products.length - 4}</div>
                      )}
                    </div>
                  )}

                  {/* PRODUCT NAMES */}
                  {products.length > 0 && (
                    <div className="ol-product-names">
                      {products.slice(0, 2).map((p: any, i: number) => (
                        <span key={i} className="ol-product-name">
                          {p.perfumeTitle ?? p.name}
                          {i === 0 && products.length > 1 ? "," : ""}
                        </span>
                      ))}
                      {products.length > 2 && (
                        <span className="ol-product-name muted">
                          +{products.length - 2} more items
                        </span>
                      )}
                    </div>
                  )}

                  {/* BOTTOM ROW */}
                  <div className="ol-card-bottom">
                    <div className="ol-payment-info">
                      <span className="ol-total">₹{na(total)}</span>
                      <span className="ol-payment-type ol-qty">
                        Qty: {na(order.totalQuantity)}
                      </span>
                      {payment && (
                        <span className="ol-payment-type">
                          {payment === "COD" ? "Cash on Delivery" : "Online Payment"}
                        </span>
                      )}
                    </div>
                    <RightOutlined className="ol-arrow" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalOrdersList;